// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useMemo } from 'react';

import type { Shipment, ShipmentStateName, ShopRole } from '@evolvemep/api-ts';

import { isNil } from 'helpers/isNotNil';
import { strongEntries } from 'helpers/strongEntries';
import { useShipmentStatuses } from 'hooks/typeCacheHooks/useShipmentStatuses';
import { useUser } from 'hooks/user/useUser';

export type ShipmentAction =
  | 'editNameDescription'
  | 'editShippingInfo'
  | 'editDates'
  | 'editAssignees'
  | 'addEditDeleteItems'
  | 'editStatus'
  | 'markReady'
  | 'markShipped'
  | 'markDelivered'
  | 'revertToDraft'
  | 'revertToReady'
  | 'revertToShipped'
  | 'delete';

type ShipmentTruthByRole = Partial<Record<ShopRole, readonly ShipmentStateName[]>>;

const shipmentTruthTable: Record<ShipmentAction, readonly ShipmentStateName[] | ShipmentTruthByRole> = {
  // Any user (state-gated only)
  editNameDescription: ['Draft', 'Ready to Ship'],
  editShippingInfo: ['Draft', 'Ready to Ship'],
  editDates: ['Draft', 'Ready to Ship'],
  editAssignees: ['Draft', 'Ready to Ship'],
  addEditDeleteItems: ['Draft', 'Ready to Ship'],
  editStatus: ['Draft', 'Ready to Ship', 'Shipped', 'Delivered'],
  // FullAccess / LimitedAccess (keep in sync with backend ShipmentTruthTable)
  markReady: { ['Shop.FullAccess']: ['Draft'], ['Shop.LimitedAccess']: ['Draft'] },
  markShipped: {
    ['Shop.FullAccess']: ['Draft', 'Ready to Ship'],
    ['Shop.LimitedAccess']: ['Draft', 'Ready to Ship'],
  },
  markDelivered: { ['Shop.FullAccess']: ['Shipped'], ['Shop.LimitedAccess']: ['Shipped'] },
  revertToDraft: { ['Shop.FullAccess']: ['Ready to Ship'] },
  revertToReady: { ['Shop.FullAccess']: ['Shipped'] },
  revertToShipped: { ['Shop.FullAccess']: ['Delivered'] },
  delete: { ['Shop.FullAccess']: ['Draft'], ['Shop.LimitedAccess']: ['Draft'] },
} as const;

/**
 * Given an action, returns the state IDs that are allowed for the action.
 * Useful for filtering shipments based on what actions can be performed on them.
 * @param action a shipment action.
 * @returns a list of state IDs that are allowed for the action, or null if the states are not loaded yet.
 */
export const useShipmentStatusFilterIds = (action: ShipmentAction) => {
  const { states } = useShipmentStatuses();
  return useMemo(() => {
    if (isNil(states)) return null;

    const truth = shipmentTruthTable[action];
    const stateNames = Array.isArray(truth)
      ? truth
      : strongEntries(truth as ShipmentTruthByRole).flatMap(([, stateName]) => stateName);
    return states.filter(({ name }) => stateNames.includes(name)).map(({ stateId }) => stateId);
  }, [states, action]);
};

export const useCanEditShipment = <T extends Pick<Shipment, 'shipmentStateName'> | undefined>(
  shipment?: T,
): {
  /**
   * If `shipment` was not passed into the hook, it must be passed into this `canEditShipment` call.
   */
  canEditShipment: T extends undefined
    ? (action: ShipmentAction, shipment: Pick<Shipment, 'shipmentStateName'>) => boolean
    : (action: ShipmentAction) => boolean;
} => {
  const { userHasRoles } = useUser();
  const shipmentStateName = useMemo(() => shipment?.shipmentStateName, [shipment]);

  const canEditShipment = useCallback(
    (action: ShipmentAction, overrideShipment?: Pick<Shipment, 'shipmentStateName'>) => {
      const state = overrideShipment?.shipmentStateName ?? shipmentStateName;
      if (!state) {
        throw new Error('Shipment was not passed in.');
      }
      const truth = shipmentTruthTable[action];
      if (Array.isArray(truth)) {
        return truth.includes(state);
      }
      return strongEntries(truth as ShipmentTruthByRole)
        .filter(([role]) => userHasRoles(role))
        .flatMap(([, states]) => states)
        .includes(state);
    },
    [userHasRoles, shipmentStateName],
  );

  return { canEditShipment };
};
